import Head from 'next/head';
import Link from 'next/link';
import { ReactSVG } from 'react-svg';
import key from 'weak-key';
import { groupBy } from 'lodash';
import { request } from 'graphql-request';
import StickyBox from 'react-sticky-box';
import dynamic from 'next/dynamic';
import { get, has, getStructuredData, getContentPaths, getAlias, getIcon, getDrupalSettings, getBlocksFromQueue } from '~/utils/utils';
import BodyParser from '~/components/bodyParser/bodyParser';
import PageWrapper from '~/components/wrappers/pageWrapper';
import ParallaxHeader from '~/components/parallaxHeader/parallaxHeader';
import RenderAt from '~/components/renderAt/renderAt';
import Share from '~/components/share/share';
import SiteSettings from '~/siteSettings';
import Skimlinks from '~/components/skimlinks/skimlinks';
import TaxLinks from '~/components/taxLinks/taxLinks';
import BlockParser from '~/components/blockParser/blockParser';
import { mobileCheck } from '~/utils/utils';

// GQL
import GET_RECIPE from '~/graphql/GET_RECIPE.graphql';
import { v1 as uuidv1 } from "uuid";
import {getVideoKeywords} from "../../utils/utils";

const Utag = dynamic(() => import('~/components/utag/utag'), { ssr: false });
const CmvAvia = dynamic(() => import('@cbsi-ctd/cmv_avia').then((mod) => mod.Player), { ssr: false });

/**
 * Recipe
 * @category Page
 * @extends Component
 * @summary Display the recipe page.
 * @return {Node} React node containing the recipe page.
 */

const Recipe = props => {
  const { router, sitePrimaryRsid, baseUrl, fileUrl, data, drupalSettings, blocks } = props;
  const path = get(router, 'query.alias')
    ? `/recipe/${ router.query.alias.join('/') }`
    : '/';

  const getSidebar = (content) => {
    if(!content?.field_recipe_ingredients) {
      return false;
    }

    const grouped = groupBy(content.field_recipe_ingredients, 'group');

    return(
      <div>
        <h4>ingredients</h4>
        { Object.entries(grouped).map((ingredientGroup) => (
          <div key={`group-${key(ingredientGroup)}`}>
            <h2>{ ingredientGroup[0] }</h2>
            <ul key={`group-${key(ingredientGroup)}`}>
              { ingredientGroup[1].map((ingredient) => (
                <li itemProp="recipeIngredient" key={`ingredient-${key(ingredient)}`}>
                  { `${ingredient.quantity} ${ingredient.unit} ${ingredient.name} ${ ingredient.comment }` }
                </li>
              )) }
            </ul>
          </div>
        )) }
      </div>
    );
  }


  const content = {
    ...data.nodeQuery
  };

  const keywords = content.field_tags && content.field_tags.map(tag => tag.name).join(',')

  const metadata = {
    assetId: content.nid,
    assetPubDate: content?.field_display_date || null,
    assetTitle: content.title,
    assetType: 'content_video',
    authorName: 'The Drew Barrymore Show Staff',
    brandPlatformId: mobileCheck() ? 'drewbarrymore_site_mweb' : 'drewbarrymore_site_desktop',
    category: content?.field_category?.name || 'qos',
    contentBrand: SiteSettings.siteName,
    deviceType: mobileCheck() ? 'mobile' : 'desktop',
    duration: content?.field_video?.field_video_mpx_id.duration,
    gaAccount: 'UA-1072526-1',
    mediaContentType: 'vod',
    mediaAutoplayCustom: true,
    mediaId: content?.field_video_reference_id || content.reference_id || content?.field_video?.field_video_mpx_id?.reference_id,
    mii: {
      viewtime: '10',
      refreshcap: '1000',
    },
    pageName: `${SiteSettings.brand}:${router.asPath}`,
    pageURL: router.asPath,
    pageType: 'recipe',
    pageViewGuid: uuidv1(),
    parentNid: content.nid,
    section: 'recipe',
    siteCode: SiteSettings.brand,
    siteHier: 'recipe|recipe',
    sitePrimaryRsid: 'cbsidrewbarrymoresite',
    siteSection: 'recipe',
    siteType: 'responsive_web',
    thumbnail: `${ content?.field_video_mpx_id?.thumbnails?.[0].streamingUrl || content?.field_video?.field_video_mpx_id?.thumbnails?.[0].streamingUrl }?width=720&crop=16:9`,
    userState: 'not authenticated',
    userType: 'anon',
    videoAutoPlay: 'on',
    videoExists: 'true',
    videoId: content?.field_video_reference_id || content.reference_id || content?.field_video?.field_video_mpx_id?.reference_id,
    videoReferenceId: content?.field_video_reference_id || content.reference_id || content?.field_video?.field_video_mpx_id?.reference_id,
    videoTitle: content?.title,
    vidPlayerPos: 'hero',
  }

  let continuousPlay = 0;
  const onContentComplete = () => {
    continuousPlay = 1;
  }

  const getVideo = (content) => {
    return(
      <>
        { content.field_video && has(content, 'field_video.field_video_mpx_id.hls_streaming_url') &&
          <h3>watch</h3>
        }
        <div className='content__embedded-player'>
          { content.field_video && has(content, 'field_video.field_video_mpx_id.hls_streaming_url') &&
            <div className='content__embedded-player-player'>
              <CmvAvia
                autoplay='attemptUnmutedThenMuted'
                captions={content?.field_video?.field_video_captions?.path}
                compactViewBreakpoint={480}
                continuousPlay={continuousPlay}
                enableOm
                fms={drupalSettings?.videoSettingsQuery?.enable_fms}
                hideAds={content.field_is_promotional}
                keywords={getVideoKeywords(content)}
                mediaUrl={content.field_video.field_video_mpx_id.hls_streaming_url}
                metadata={metadata}
                onContentComplete={onContentComplete}
                posterImageUrl={`${ content?.field_video?.field_video_mpx_id?.thumbnails?.[0].streamingUrl }?width=720&crop=16:9`}
                siteSettings={SiteSettings}
                useUnmuteCta={true}
              />
            </div>
          }
          {/* Subhead */}
          <div className='recipe__btn-related'>
            { content.field_subhead &&
              <h3>
                { content.field_subhead }
              </h3>
            }
            <ReactSVG src='/img/icons/recipe.svg' />
            <a href='#related'>
              See related recipes
            </a>
          </div>
        </div>
      </>
    )
  }

  const convertTimes = (t) => {
    const time = (t).substring(2);
    const getHours = parseInt(time.match(/([0-9])+H/g), 10);
    const getMin = parseInt(time.match(/([0-9])+M/g), 10);
    let formattedTime = '';

    if(getHours) {
      formattedTime += `${ getHours } ${ getHours > 1 ? 'hrs' : 'hr' }`;
    }

    if(getMin) {
      formattedTime += `${getMin && ' '}${ getMin } ${ getMin > 1 ? 'mins' : 'min' }`;
    }

    return formattedTime;
  }

  return (
    <>
      <Head>
        <title>{ content.title } | { SiteSettings.siteName }</title>
        <meta name='title' content={ content.title } />
        <meta name='twitter:card' content='summary_large_image' />
        <meta name='twitter:title' content={ content.field_social_headline || content.title } />
        <meta name='description' content={ content.field_seo_description || content.field_subhead } />
        <meta property='og:url' content={ path } />
        <meta property='og:title' content={ content?.field_social_headline || content.title } />
        <meta property='og:image' content={ content?.field_image?.field_image?.social?.url } />
        <meta property='og:description' content={ content?.field_social_description || content?.field_subhead } />
        <link rel='canonical' href={`${ baseUrl }${ path }`} />
        <link rel='image_src' href={ content?.field_image?.field_image?.social.url } />
        <meta name='twitter:url' content={ path } />
        <meta name='twitter:image:width' content={ content?.field_image?.field_image?.social?.width } />
        <meta name='twitter:image:height' content={ content?.field_image?.field_image?.social?.height } />
        <meta name='twitter:image' content={ content?.field_image?.field_image?.social?.url } />
        <meta property='article:published_time' content={ content?.field_display_date } />
        <meta name='keywords' content={ keywords || SiteSettings.defaultKeywords } />
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{ __html: JSON.stringify(getStructuredData(baseUrl, path, content)) }}
        />
        <meta name='robots' content={`${content.field_is_promotional ? 'noindex' : 'index'}, max-image-preview:large`} />
      </Head>
      <div className='content' itemScope itemType='http://schema.org/Recipe'>
        {/* Main Image */}
        <ParallaxHeader>
          <div className='content__header-img'>
            { content?.field_image?.field_image &&
              <picture>
                <source media={ `(max-width: ${ SiteSettings.breakpoints.sm })` } srcSet={ content.field_image.field_image.sm.url } />
                <img
                  src={ content.field_image.field_image.lg.url }
                  alt={ content.field_image?.alt }
                  width={ content.field_image.field_image.lg.width }
                  height={ content.field_image.field_image.lg.height }
                  className='img-full'
                  loading='lazy'
                  itemProp='image'
                />
              </picture>
            }
            { !content?.field_image?.field_image &&
              <div className='img-missing'>No Image Available</div>
            }
          </div>
        </ParallaxHeader>
        <div className='site-bg'>
          <div className='constrain constrain--bg'>
            <div className='recipe__grid'>
              <div className='recipe__grid-content'>
                {/* Title */}
                <h1 className='content__title' itemProp='name'>
                  { content.title }
                </h1>
                {/* Is Promotional Flag */}
                { content.field_is_promotional && content.field_flag &&
                  <div className='content__flag'>
                    { content.field_flag }
                  </div>
                }
                {/* Category */}
                { content.field_category && content.field_category.path &&
                  <div className='content__category'>
                    <Link legacyBehavior href={ content.field_category.path }>
                      { content.field_category.name }
                    </Link>
                  </div>
                }
                {/* Category (if no url) */}
                { content.field_category && content.field_category.path === undefined &&
                  <div className='content__category'>
                    { content.field_category.name }
                  </div>
                }
                {/* Skimlinks */}
                { content.field_skimlinks &&
                  <Skimlinks
                    type={ content.__typename }
                    data={ drupalSettings }
                  />
                }
                {/* Recipe Summary */}
                { content.field_recipe_summary &&
                  <div itemProp='description' className='recipe__summary' dangerouslySetInnerHTML={{ __html: content.field_recipe_summary.replace(', full_html', '') }}></div>
                }
                <RenderAt operator='lt' trigger={ 800 }>
                  <div className='recipe-video-mobile'>
                    <div className='recipe-share'>
                      <Share
                        title={ content.title }
                      />
                    </div>
                    { getVideo(content) }
                  </div>
                </RenderAt>
                {/* Recipe Times */}
                <div className='recipe__durations'>
                  <div className='recipe__durations-grid'>
                    { content.field_recipe_prep_time &&
                      <div itemProp='prepTime' content={ content.field_recipe_prep_time }>
                        { convertTimes(content.field_recipe_prep_time) }
                        <span>Prep Time</span>
                      </div>
                    }
                    { content.field_recipe_cook_time &&
                      <div itemProp='cookTime'  content={ content.field_recipe_cook_time }>
                        { convertTimes(content.field_recipe_cook_time) }
                        <span>Cook Time</span>
                      </div>
                    }
                  </div>
                  <hr />
                  <div className='recipe__durations-grid'>
                    { content.field_recipe_total_time &&
                      <div itemProp='totalTime' content={ content.field_recipe_total_time }>
                        { convertTimes(content.field_recipe_total_time) }
                        <span>Total Time</span>
                      </div>
                    }
                  </div>
                </div>
                {/* Ingredients */}
                <div className='recipe__ingredients'>
                  { getSidebar(content) }
                </div>
                {/* Recipe Instructions */}
                { content.field_recipe_instructions &&
                  <div itemProp='recipeInstructions' className='recipe__instructions'>
                    <h4>prep</h4>
                    <div dangerouslySetInnerHTML={{ __html: content.field_recipe_instructions.replace(', full_html', '') }}></div>
                  </div>
                }
                {/* Body */}
                { content.body &&
                  <div className='content__body-main'>
                    <BodyParser
                      baseDomain={ fileUrl }
                      content={ content.body }
                      drupalSettings={ drupalSettings }
                    />
                  </div>
                }
                <div className='content__spacer'></div>
                { blocks?.entitySubQueueQuery?.items &&
                  <BlockParser
                    blockItems={ blocks.entitySubQueueQuery.items }
                  />
                }
                <img
                  src='/img/drew_heart_related.png'
                  alt=''
                  width='40'
                  height='42'
                />
                <div className='content__spacer'></div>
                <TaxLinks content={ content } />
              </div>
              <RenderAt operator='gt' trigger={ 800 }>
                <aside className='recipe__grid-aside'>
                  <StickyBox enabled='true' offsetTop={ 100 } offsetBottom={ 0 }>
                    <div>
                      <div className='recipe-share'>
                        <Share
                          title={ content.title }
                          hasPrint
                        />
                      </div>
                      { getVideo(content) }
                    </div>
                  </StickyBox>
                </aside>
              </RenderAt>
            </div>
          </div>
        </div>
        {/* Related Content */}
        { content.field_related_content && content.field_related_content.length > 0 &&
          <div className='recipes__related'>
            <div className='constrain'>
              <a name='related'>
                <h3>related recipes</h3>
              </a>
            </div>
            { content.field_related_content.map((related) => (
              <Link legacyBehavior href={ related.path } key={`tag-${key(related)}`}>
                <a aria-label={`Visit ${ related.title }`}>
                  <div>
                    { related.path &&
                      <div className='wrap-relative'>
                        <div className='constrain constrain--static'>
                          <Link legacyBehavior href={ related.path } passHref>
                            <a>
                              <div className='recipes__related-cta'>
                                <ReactSVG src={ getIcon(related.__typename) } />
                                { related.title }
                              </div>
                            </a>
                          </Link>
                        </div>
                        <picture>
                          <source media={ `(max-width: ${ SiteSettings.breakpoints.sm })` } srcSet={ related.field_image.sm.url } />
                          <img
                            src={ related.field_image.lg.url }
                            alt={ related.field_image.alt }
                            width={ related.field_image.lg.width }
                            height={ related.field_image.lg.height }
                            className='recipes__related-bg'
                            loading='lazy'
                          />
                        </picture>
                      </div>
                    }
                  </div>
                </a>
              </Link>
            )) }
          </div>
        }
      </div>
      <Utag
        pageType='recipe'
        siteSection={ content?.field_category?.name }
        data={{
          sitePrimaryRsid,
          editorialContentName: content.title,
          editorialContentId: content.nid,
          ...(keywords && {keywords})
        }}
      />
    </>
  );
}

export async function getStaticPaths() {
  const paths = await getContentPaths('/recipe/', {
    machine_name: 'latest_content_by_type',
    display: 'master',
    itemsPerPage: parseInt(process.env.PRERENDER_COUNT, 10) || 10,
    offset: 0,
    viewArgs: [
      'recipe'
    ]
  });

  return {
    paths,
    fallback: 'blocking'
  };
}

export async function getStaticProps(ctx) {
  const aliasData = await getAlias({
    alias: `/recipe/${ ctx.params.alias.join('/') }`
  });

  if(!aliasData?.pathAliasQuery?.nid) {
    return {
      notFound: true,
      revalidate: 1
    }
  }

  // Get Data
  const data = await request(process.env.NEXT_PUBLIC_API_HOST || 'https://api-stage.thedrewbarrymoreshow.com/cmv_graphql/api/query', GET_RECIPE, {
    target_id: parseInt(aliasData.pathAliasQuery.nid, 10)
  });

  if(!data?.nodeQuery) {
    return {
      notFound: true,
      revalidate: 1
    }
  }

  // Get Blocks
  const blocks = await getBlocksFromQueue({
    target_id: 'recipe_blocks',
    limit: 100
  });

  // Get Site Settings
  const drupalSettings = await getDrupalSettings();

  return {
    props: {
      data,
      blocks,
      drupalSettings
    },
    revalidate: 1
  }
}

export default PageWrapper(Recipe);